import { Injectable } from '@angular/core';
import { COOKIE_CONSENT_COOKIE_NAME } from '@services/common/consent.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ConfigService } from 'app/app-config.service';
import { NgcCookieConsentService } from 'ngx-cookieconsent';

/**
 * Privacy policy on the public site, per language. The slugs are localised and
 * do not follow a pattern, so they are listed rather than built. Czech has no
 * variant yet and falls back to English.
 *
 * The `#7-cookies` fragment lands the reader on the cookies section instead of
 * the top of a nine-section notice. The section is titled "Cookies" in all three
 * languages, so the generated anchor is identical everywhere — but it does encode
 * the section number: renumbering that page (d-carbonize-website) breaks these
 * links.
 */
export const PRIVACY_POLICY_URL: Record<string, string> = {
    en: 'https://d-carbonize.eu/data-privacy/#7-cookies',
    fr: 'https://d-carbonize.eu/fr/politique-de-confidentialite/#7-cookies',
    nl: 'https://d-carbonize.eu/nl/gegevensprivacy/#7-cookies',
};
const DEFAULT_PRIVACY_POLICY_URL = PRIVACY_POLICY_URL['en'];

/** The privacy policy for a language, falling back to English where there is no variant. */
export function privacyPolicyUrl(lang: string | undefined): string {
    return (lang && PRIVACY_POLICY_URL[lang]) ?? DEFAULT_PRIVACY_POLICY_URL;
}

/**
 * Consent cookies written by an earlier version of the notice. They are no
 * longer read — the current name is `COOKIE_CONSENT_COOKIE_NAME` — but a stale
 * `cookieconsent_status` would otherwise sit in the browser for its full year,
 * which is exactly the kind of leftover a cookie notice is supposed to prevent.
 */
const LEGACY_CONSENT_COOKIE_NAMES = ['cookieconsent_status'];

@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    /**
     * Constructor
     *
     * @param configService
     * @param _translateService
     * @param _cookieConsentService
     */
    constructor(
        private configService: ConfigService,
        private _translateService: CustomTranslateService,
        private _cookieConsentService: NgcCookieConsentService,
    ) {
        this.clearLegacyConsentCookies();
        this.updateCookiePopup();
        this._translateService.onLangChange.subscribe(() => this.updateCookiePopup());
    }

    private get cookieDomain(): string {
        return this.configService.getConfig.domain ?? 'localhost';
    }

    /**
     * Expires the consent cookies of previous notice versions. Written both
     * host-only and on the configured domain, because that is how they may have
     * been set: a cookie is only overwritten by an expiry carrying the same
     * domain and path.
     */
    private clearLegacyConsentCookies(): void {
        for (const name of LEGACY_CONSENT_COOKIE_NAMES) {
            for (const domain of ['', `;domain=${this.cookieDomain}`]) {
                document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT${domain}`;
            }
        }
    }

    updateCookiePopup() {
        this._cookieConsentService.getConfig().content = {
            ...this._cookieConsentService.getConfig().content,
            message:
                `<span class="cc-dcar-title">${this._translateService.instant('cookie.title')}</span>` +
                `${this._translateService.instant('cookie.message')}`,
            allow: this._translateService.instant('cookie.allow'),
            deny: this._translateService.instant('cookie.deny'),
            link: this._translateService.instant('cookie.link'),
            href: privacyPolicyUrl(this._translateService.currentLang),
            // External site: keep the app (and the pending consent choice) open.
            target: '_blank',
            policy: this._translateService.instant('cookie.policy'),
        };

        this._cookieConsentService.getConfig().cookie = {
            ...this._cookieConsentService.getConfig().cookie,
            name: COOKIE_CONSENT_COOKIE_NAME,
            domain: this.cookieDomain,
        };

        this._cookieConsentService.destroy();
        this._cookieConsentService.init(this._cookieConsentService.getConfig());
    }
}
